/* Progressive enhancement only. Liquid owns the links, content and form data. */
(() => {
  'use strict';
  class DHShopifyNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const controller = this.controller;
      const $ = s => this.querySelector(s);
      const on = (el, type, fn) => el?.addEventListener(type, fn, {signal: this.controller.signal});
      this.trigger = $('.dh-nav-trigger');
      this.panel = $('.dh-nav-panel');
      this.drawer = $('.dh-nav-drawer');
      this.opener = $('.dh-nav-mobile-open');
      this.desktop = matchMedia('(min-width:1024px)');
      this.hover = matchMedia('(hover:hover) and (pointer:fine)');
      this.menuOpen = false;
      this.pinned = false;
      this.suppress = false;
      // Hover bridge (2026-09-26): an invisible strip that fills the whole gap between the
      // Shop button and the panel, so moving the pointer down never "leaves" the menu.
      if (this.panel && !this.panel.querySelector('.dh-nav-bridge')) {
        const bridge = document.createElement('div');
        bridge.className = 'dh-nav-bridge';
        bridge.setAttribute('aria-hidden', 'true');
        this.panel.prepend(bridge);
      }
      const sizeBridge = () => {
        const bridge = this.panel?.querySelector('.dh-nav-bridge');
        if (!bridge || !this.trigger) return;
        const gap = Math.max(12, Math.ceil(this.panel.getBoundingClientRect().top - this.trigger.getBoundingClientRect().bottom) + 4);
        bridge.style.cssText = `position:absolute;left:0;right:0;top:-${gap}px;height:${gap}px;`;
      };
      const cancelLeave = () => clearTimeout(this.leaveTimer);
      const enter = () => {
        cancelLeave();
        if (this.hover.matches && !this.suppress) this.setMenu(true);
        sizeBridge();
      };
      const leave = () => {
        this.suppress = false;
        cancelLeave();
        this.leaveTimer = setTimeout(() => {
          if (!this.pinned && !this.panel.contains(document.activeElement)) this.setMenu(false);
        }, 400);
      };
      on(this.trigger, 'pointerenter', enter);
      on(this.trigger, 'pointerleave', leave);
      on(this.panel, 'pointerenter', cancelLeave);
      on(this.panel, 'pointerleave', leave);
      on(window, 'resize', sizeBridge);
      on(this.trigger, 'click', e => {
        cancelLeave();
        sizeBridge();
        if (!e.detail || !this.hover.matches) {
          this.pinned = !this.menuOpen;
          this.setMenu(!this.menuOpen);
        } else if (this.pinned) {
          this.pinned = false;
          this.suppress = true;
          this.setMenu(false);
        } else {
          this.pinned = true;
          this.setMenu(true);
        }
      });
      on(this.trigger, 'keydown', e => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.pinned = true;
          this.setMenu(true);
          this.panel.querySelector('a')?.focus();
        }
      });
      on(document, 'pointerdown', e => {
        if (!e.composedPath().includes($('.dh-nav-desktop-menu'))) this.resetMenu();
      });
      on(document, 'focusin', e => {
        if (!$('.dh-nav-desktop-menu').contains(e.target)) this.resetMenu();
      });
      on(document, 'keydown', e => {
        if (e.key === 'Escape' && this.menuOpen) {
          e.preventDefault();
          const restore = this.panel.contains(document.activeElement);
          this.resetMenu();
          this.suppress = true;
          if (restore) this.trigger.focus();
        }
      });
      on(this.opener, 'click', () => this.openDrawer());
      on($('.dh-nav-close'), 'click', () => this.closeDrawer());
      on(this.drawer, 'cancel', e => { e.preventDefault(); this.closeDrawer(); });
      on(this.drawer, 'click', e => {
        if (e.target !== this.drawer) return;
        const r = this.drawer.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.closeDrawer();
      });
      on(this.drawer, 'keydown', e => {
        if (e.key !== 'Tab') return;
        const list = [...this.drawer.querySelectorAll('a,button,input,select,summary')]
          .filter(el => el.getClientRects().length && !el.disabled);
        const first = list[0], last = list.at(-1);
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      on(this.drawer, 'close', () => {
        this.restoreBody();
        this.opener.setAttribute('aria-expanded', 'false');
      });
      on(this.desktop, 'change', () => {
        this.resetMenu();
        if (this.desktop.matches) this.closeDrawer(true);
      });
      // Mobile header search (Sept 2026): open the theme's existing predictive-search dialog;
      // the element is a real link to the search page, so it still works if the dialog is missing.
      this.querySelectorAll('[data-dh-search-open]').forEach(el => el.setAttribute('aria-haspopup', 'dialog'));
      on(this, 'click', e => {
        const opener = e.target.closest('[data-dh-search-open]');
        if (!opener || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        const modal = document.getElementById('search-modal');
        if (typeof modal?.showDialog !== 'function') return;
        e.preventDefault();
        this.resetMenu();
        if (this.drawer?.open) this.closeDrawer(true);
        try { modal.showDialog(); } catch { window.location.href = opener.href; }
      });
      on(this, 'click', e => {
        const link = e.target.closest('a');
        if (!link || e.defaultPrevented) return;
        this.resetMenu();
        if (this.drawer.open) this.closeDrawer(true);
        if (!link.hasAttribute('data-cart-link') || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
        if (this.dataset.useCartDrawer === 'true' && typeof cartDrawer?.open === 'function') {
          try { cartDrawer.open(); e.preventDefault(); this.refreshCart(); }
          catch { /* Keep the real href as a fallback. */ }
        }
      });
      this.querySelectorAll('[data-country]').forEach(select => on(select, 'change', () => select.form.requestSubmit()));
      const refresh = () => this.queueCartRefresh();
      on(window, 'focus', refresh);
      on(window, 'pageshow', refresh);
      on(document, 'duskhaven:cart-updated', refresh);
      on(document, 'cart:updated', refresh);
      // Adapt to Clarity's published event constants, not a guessed event name.
      const hasShopifyEvents = [...document.querySelectorAll('script[type="importmap"]')]
        .some(script => script.textContent.includes('"@shopify/events"'));
      if (hasShopifyEvents) {
        import('@shopify/events').then(({StandardEvents}) => {
          if (!this.isConnected || this.controller !== controller || controller.signal.aborted) return;
          on(document, StandardEvents.cartLinesUpdate, event => {
            if (event.promise) Promise.resolve(event.promise).then(refresh, refresh);
            else refresh();
          });
        }).catch(() => { /* Generic custom event and focus refresh remain available. */ });
      }
      on(document, 'shopify:block:select', e => {
        if (!this.contains(e.target)) return;
        if (this.desktop.matches) { this.pinned = true; this.setMenu(true); }
        else this.openDrawer();
      });
      on(document, 'shopify:section:deselect', e => {
        if (!e.target.contains(this)) return;
        this.resetMenu();
        this.closeDrawer(true);
      });
      this.dataset.enhanced = 'true';
    }
    setMenu(open) {
      this.menuOpen = Boolean(open && this.desktop.matches);
      this.panel.dataset.open = String(this.menuOpen);
      this.panel.inert = !this.menuOpen;
      this.trigger.setAttribute('aria-expanded', String(this.menuOpen));
    }
    resetMenu() { clearTimeout(this.leaveTimer); this.pinned = false; this.setMenu(false); }
    openDrawer() {
      if (this.drawer.open || this.desktop.matches) return;
      this.resetMenu();
      clearTimeout(this.closeTimer);
      this.previousOverflow = document.body.style.overflow;
      this.previousPadding = document.body.style.paddingRight;
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (gap) document.body.style.paddingRight = `${gap}px`;
      this.bodyLocked = true;
      this.drawer.showModal();
      this.opener.setAttribute('aria-expanded', 'true');
      this.drawer.getBoundingClientRect();
      this.drawer.dataset.open = 'true';
      this.querySelector('.dh-nav-close').focus();
    }
    closeDrawer(immediate = false) {
      if (!this.drawer.open) return;
      clearTimeout(this.closeTimer);
      this.drawer.dataset.open = 'false';
      const finish = () => {
        this.drawer.close();
        this.restoreBody();
        this.opener.setAttribute('aria-expanded', 'false');
        if (this.isConnected && !this.desktop.matches) this.opener.focus();
      };
      if (immediate || matchMedia('(prefers-reduced-motion:reduce)').matches) finish();
      else this.closeTimer = setTimeout(finish, 300);
    }
    restoreBody() {
      if (!this.bodyLocked) return;
      document.body.style.overflow = this.previousOverflow;
      document.body.style.paddingRight = this.previousPadding;
      this.bodyLocked = false;
    }
    queueCartRefresh() {
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(() => this.refreshCart(), 100);
    }
    async refreshCart() {
      this.cartRequest?.abort();
      this.cartRequest = new AbortController();
      try {
        const response = await fetch(this.dataset.cartUrl, {
          headers: {Accept: 'application/json'}, credentials: 'same-origin',
          cache: 'no-store', signal: this.cartRequest.signal
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (!Number.isInteger(cart.item_count) || !this.isConnected) return;
        const count = this.querySelector('[data-cart-count]');
        const changed = count.textContent.trim() !== String(cart.item_count);
        count.textContent = cart.item_count;
        const label = `${this.dataset.cartLabel}: ${cart.item_count}`;
        this.querySelector('[data-cart-link]').setAttribute('aria-label', label);
        if (changed) this.querySelector('[data-cart-status]').textContent = label;
      } catch { /* Preserve the last valid server-rendered count on failure. */ }
    }
    disconnectedCallback() {
      for (const timer of [this.leaveTimer, this.closeTimer, this.cartTimer]) clearTimeout(timer);
      if (this.drawer?.open) this.drawer.close();
      this.restoreBody();
      this.cartRequest?.abort();
      this.controller?.abort();
      this.controller = null;
      delete this.dataset.enhanced;
    }
  }
  if (!customElements.get('dh-shopify-navigation')) customElements.define('dh-shopify-navigation', DHShopifyNavigation);
})();